import React from "react";
import { RefreshCw, User } from "lucide-react";
import { Modal } from "../shared/Modal";
import { StatusBadge } from "../shared/StatusBadges";
import { useAppContext } from "../../AppContext";

/* -----------------------------------------------------------------------------
 * UserProfileModal — admin-side "Historial del Cliente" modal shown when the
 * admin clicks a client row. Displays the user's profile, aggregate stats
 * and the full list of their orders. Extracted verbatim from AppModals.jsx.
 * --------------------------------------------------------------------------- */
export function UserProfileModal() {
  const ctx = useAppContext();
  const {
    clientHistory, setClientHistory, clientHistoryLoading,
    formatBs, formatUsd,
  } = ctx;

  if (!clientHistory) return null;

  return (
        <Modal onClose={() => setClientHistory(null)} size="lg" testId="client-history-modal">
          <div className="space-y-5">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-white">Historial del Cliente</h3>
                  <p className="text-[11px] font-mono text-zinc-400 truncate">
                    {clientHistoryLoading ? "Cargando…" : (clientHistory.user?.email || "—")}
                  </p>
                </div>
              </div>
            </div>

            {clientHistoryLoading || !clientHistory.stats ? (
              <div className="text-center py-8 text-xs text-zinc-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Cargando historial…
              </div>
            ) : (
              <>
                {/* Stats grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-xl bg-zinc-950 border border-zinc-800">
                    <p className="text-[10px] font-mono uppercase text-zinc-500">Total pedidos</p>
                    <p className="text-xl font-extrabold text-white font-mono" data-testid="ch-stat-total">
                      {clientHistory.stats.total_orders}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-green-950/40 border border-green-500/30">
                    <p className="text-[10px] font-mono uppercase text-green-300">Completados</p>
                    <p className="text-xl font-extrabold text-green-300 font-mono">
                      {clientHistory.stats.completed}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-red-950/40 border border-red-500/30">
                    <p className="text-[10px] font-mono uppercase text-red-300">Rechazados</p>
                    <p className="text-xl font-extrabold text-red-300 font-mono">
                      {clientHistory.stats.rejected}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-950/40 border border-purple-500/30">
                    <p className="text-[10px] font-mono uppercase text-purple-300">Saldo a favor</p>
                    <p className="text-xl font-extrabold text-purple-200 font-mono" data-testid="ch-stat-credit">
                      Bs {Math.round(clientHistory.stats.credit_balance_bs)}
                    </p>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-black/40 border border-zinc-800 text-[11px] font-mono flex flex-wrap items-center justify-between gap-2">
                  <span className="text-zinc-400">
                    Total gastado (aprobado): <strong className="text-cyan-300">Bs {Math.round(clientHistory.stats.total_spent_bs)}</strong>
                  </span>
                  <span className="text-zinc-500">
                    ID: <span className="text-zinc-300">{(clientHistory.user?.id || "").slice(0, 12)}…</span>
                  </span>
                </div>

                {/* Orders list */}
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">
                    Pedidos ({clientHistory.orders.length})
                  </p>
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="ch-orders-list">
                    {clientHistory.orders.length === 0 ? (
                      <p className="text-center text-xs text-zinc-500 py-4">Este cliente aún no tiene pedidos.</p>
                    ) : (
                      clientHistory.orders?.map(o => (
                        <div
                          key={o.id}
                          data-testid={`ch-order-${o.id}`}
                          className="flex items-center justify-between gap-3 p-3 rounded-xl bg-zinc-950 border border-zinc-800"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-cyan-400 text-xs font-bold">{o.id}</span>
                              <StatusBadge status={o.status} />
                            </div>
                            <p className="text-xs text-white mt-0.5 truncate">
                              {o.gameName} · <span className="text-zinc-400">{o.packageName}</span>
                            </p>
                            <p className="text-[10px] font-mono text-zinc-500">
                              {new Date(o.createdAt).toLocaleString("es-VE")}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-cyan-300 font-mono">Bs {Math.round(o.paidAmountBs)}</p>
                            {o.surplusBs > 0 && (
                              <p className={`text-[10px] font-mono ${o.surplusCredited ? "text-green-400" : "text-amber-400"}`}>
                                + Bs {Math.round(o.surplusBs)} excedente{o.surplusCredited ? "" : " (por validar)"}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="pt-2 border-t border-zinc-800">
              <button
                onClick={() => setClientHistory(null)}
                data-testid="close-client-history-btn"
                className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-2.5 rounded-xl text-xs"
              >
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
  );
}
